import { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { NavLink } from 'react-router-dom';
import {
  InputText,
  ToggleInput,
  ToggleSize,
  Checkbox,
  IconInfo,
  PermissionEnum,
  IconChangeLoClock,
} from '@ui/src';
import { useAppSelector } from '@store';
import { CreateBlacklistFormTypes } from './types';
import SettingItem from './settings-item';

import cn from './blacklist.module.css';

const customersAndApplication = [
  {
    title: 'Corporate clients',
    value: 'corporateClients',
    subTypes: [
      {
        title: 'Adverse Media',
        value: 'corporateClientsAdverseMedia',
      },
      {
        title: 'Enforcement',
        value: 'corporateClientsEnforcement',
      },
      {
        title: 'PEP',
        value: 'corporateClientsPEP',
      },
      {
        title: 'Sanction',
        value: 'corporateClientsSanction',
      },
      {
        title: 'SOE/SWF',
        value: 'corporateClientsSOESWF',
      },
    ],
  },
  {
    title: 'Individual clients',
    value: 'individualClients',
    subTypes: [
      {
        title: 'Adverse Media',
        value: 'individualClientsAdverseMedia',
      },
      {
        title: 'Enforcement',
        value: 'individualClientsEnforcement',
      },
      {
        title: 'PEP',
        value: 'individualClientsPEP',
      },
      {
        title: 'Sanction',
        value: 'individualClientsSanction',
      },
      {
        title: 'SOE/SWF',
        value: 'individualClientsSOESWF',
      },
    ],
  },
];

const customersInTransactions = [
  {
    title: 'Transactions - Corporate clients',
    value: 'transactionsCorporateClients',
    subTypes: [
      {
        title: 'Adverse Media',
        value: 'transactionsCorporateClientsAdverseMedia',
      },
      {
        title: 'Enforcement',
        value: 'transactionsCorporateClientsEnforcement',
      },
      {
        title: 'PEP',
        value: 'transactionsCorporateClientsPEP',
      },
      {
        title: 'Sanction',
        value: 'transactionsCorporateClientsSanction',
      },
      {
        title: 'SOE/SWF',
        value: 'transactionsCorporateClientsSOESWF',
      },
    ],
  },
  {
    title: 'Transactions - Individual clients',
    value: 'transactionsIndividualClients',
    subTypes: [
      {
        title: 'Adverse Media',
        value: 'transactionsIndividualClientsAdverseMedia',
      },
      {
        title: 'Enforcement',
        value: 'transactionsIndividualClientsEnforcement',
      },
      {
        title: 'PEP',
        value: 'transactionsIndividualClientsPEP',
      },
      {
        title: 'Sanction',
        value: 'transactionsIndividualClientsSanction',
      },
      {
        title: 'SOE/SWF',
        value: 'transactionsIndividualClientsSOESWF',
      },
    ],
  },
];

const payers = [
  {
    title: 'Transactions - Corporate payers / payees',
    value: 'transactionsCorporatePayersPayees',
    subTypes: [
      {
        title: 'Adverse Media',
        value: 'transactionsCorporatePayersPayeesAdverseMedia',
      },
      {
        title: 'Enforcement',
        value: 'transactionsCorporatePayersPayeesEnforcement',
      },
      {
        title: 'PEP',
        value: 'transactionsCorporatePayersPayeesPEP',
      },
      {
        title: 'Sanction',
        value: 'transactionsCorporatePayersPayeesSanction',
      },
      {
        title: 'SOE/SWF',
        value: 'transactionsCorporatePayersPayeesSOESWF',
      },
    ],
  },
  {
    title: 'Transactions - Individual payers / payees',
    value: 'transactionsIndividualPayersPayees',
    subTypes: [
      {
        title: 'Adverse Media',
        value: 'transactionsIndividualPayersPayeesAdverseMedia',
      },
      {
        title: 'Enforcement',
        value: 'transactionsIndividualPayersPayeesEnforcement',
      },
      {
        title: 'PEP',
        value: 'transactionsIndividualPayersPayeesPEP',
      },
      {
        title: 'Sanction',
        value: 'transactionsIndividualPayersPayeesSanction',
      },
      {
        title: 'SOE/SWF',
        value: 'transactionsIndividualPayersPayeesSOESWF',
      },
    ],
  },
];

const banksIntransactions = [
  {
    title: 'Transactions - Counterparties banks',
    value: 'transactionsCounterpartiesBanks',
    subTypes: [
      {
        title: 'Adverse Media',
        value: 'transactionsCounterpartiesBanksAdverseMedia',
      },
      {
        title: 'Enforcement',
        value: 'transactionsCounterpartiesBanksEnforcement',
      },
      {
        title: 'PEP',
        value: 'transactionsCounterpartiesBanksPEP',
      },
      {
        title: 'Sanction',
        value: 'transactionsCounterpartiesBanksSanction',
      },
      {
        title: 'SOE/SWF',
        value: 'transactionsCounterpartiesBanksSOESWF',
      },
    ],
  },
];

const paymentOrders = [
  {
    title: 'Payment order - Payers / payees',
    value: 'paymentOrderPayersPayees',
    subTypes: [
      {
        title: 'Adverse Media',
        value: 'paymentOrderPayersPayeesAdverseMedia',
      },
      {
        title: 'Enforcement',
        value: 'paymentOrderPayersPayeesEnforcement',
      },
      {
        title: 'PEP',
        value: 'paymentOrderPayersPayeesPEP',
      },
      {
        title: 'Sanction',
        value: 'paymentOrderPayersPayeesSanction',
      },
      {
        title: 'SOE/SWF',
        value: 'paymentOrderPayersPayeesSOESWF',
      },
    ],
  },
];

const CreateBlacklistForm = ({
  control,
  errors,
  defaultBlacklist,
  blacklistId,
  enableAll,
  allSettingsEnabled,
  toggleActive,
  isEditable,
  setAllSettingsEnabled,
  fields,
  settings,
  setSettings,
  changeStatusInProgress,
  settingsId,
  setValue,
}: CreateBlacklistFormTypes) => {
  const { activePermissions } = useAppSelector(
    (state) => state.amlDictionaries,
  );

  useEffect(() => {
    if (
      fields.corporateClients &&
      fields.individualClients &&
      fields.transactionsCorporateClients &&
      fields.transactionsCorporatePayersPayees &&
      fields.transactionsCounterpartiesBanks &&
      fields.transactionsIndividualClients &&
      fields.transactionsIndividualPayersPayees &&
      fields.paymentOrderPayersPayees &&
      !allSettingsEnabled
    ) {
      setAllSettingsEnabled(true);
    }
  }, [fields]);

  return (
    <div className={cn.createBlacklistForm}>
      <div className={cn.createBlacklistFormGroup}>
        <Controller
          control={control}
          name="name"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="listName"
              placeholder="Enter List Name"
              onChange={onChange}
              value={value}
              title="List name"
              error={errors?.name?.message}
              disabled={defaultBlacklist || Boolean(!isEditable && blacklistId)}
              tabIndex={
                defaultBlacklist || Boolean(!isEditable && blacklistId) ? -1 : 0
              }
            />
          )}
        />
      </div>
      <div className={cn.createBlacklistFormGroup}>
        <Controller
          control={control}
          name="description"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="description"
              placeholder="Enter description"
              onChange={onChange}
              value={value}
              title="Description (optional)"
              error={errors?.description?.message}
              disabled={Boolean(!isEditable && blacklistId)}
              tabIndex={!isEditable && blacklistId ? -1 : 0}
            />
          )}
        />
      </div>
      {blacklistId && (
        <Controller
          control={control}
          name="active"
          defaultValue={false}
          render={({ field: { value } }) => (
            <Checkbox
              name="checkbox-active"
              title="Active"
              onChange={(e) => toggleActive && toggleActive(e.target.checked)}
              type="checkbox"
              checked={value}
              disabled={
                Boolean(!isEditable && blacklistId) || changeStatusInProgress
              }
              tabIndex={!isEditable && blacklistId ? -1 : 0}
            />
          )}
        />
      )}
      <div className={cn.formBlockTitle}>
        Select the fields to be indexed from the uploaded file
      </div>
      <div className={cn.enableAllToggleWrapper}>
        <ToggleInput
          title="Enable All"
          name="enableAll"
          size={ToggleSize.MEDIUM}
          onChange={enableAll}
          checked={allSettingsEnabled}
          disabled={Boolean(!isEditable && blacklistId)}
          tabIndex={!isEditable && blacklistId ? -1 : 0}
        />
      </div>
      <div className={cn.blockTitle}>
        All customers & Applications
        <span>
          <div className={cn.blockHint}>
            Verification of the customer in the Application and further on with
            certain regularity
          </div>
          <IconInfo width={16} height={16} />
        </span>
      </div>
      <div className={cn.formToggleGroupWrapper}>
        {customersAndApplication.map((item) => (
          <SettingItem
            key={item.value}
            control={control}
            item={item}
            allSettingsEnabled={allSettingsEnabled}
            setAllSettingsEnabled={setAllSettingsEnabled}
            settings={settings}
            setSettings={setSettings}
            isEditable={isEditable}
            blacklistId={blacklistId}
            setValue={setValue}
            fields={fields}
          />
        ))}
      </div>

      <div className={cn.blockTitle}>
        Our customers in Transactions
        <span>
          <div className={cn.blockHint}>
            Verification of our clients within any type of transactions
          </div>
          <IconInfo width={16} height={16} />
        </span>
      </div>
      <div className={cn.formToggleGroupWrapper}>
        {customersInTransactions.map((item) => (
          <SettingItem
            key={item.value}
            control={control}
            item={item}
            allSettingsEnabled={allSettingsEnabled}
            setAllSettingsEnabled={setAllSettingsEnabled}
            settings={settings}
            setSettings={setSettings}
            isEditable={isEditable}
            blacklistId={blacklistId}
            setValue={setValue}
            fields={fields}
          />
        ))}
      </div>
      <div className={cn.blockTitle}>
        Payers / Payees in Transactions
        <span>
          <div className={cn.blockHint}>
            Recipient - in case of outgoing payment or Sender - in case of
            incoming payment
          </div>
          <IconInfo width={16} height={16} />
        </span>
      </div>
      <div className={cn.formToggleGroupWrapper}>
        {payers.map((item) => (
          <SettingItem
            key={item.value}
            control={control}
            item={item}
            allSettingsEnabled={allSettingsEnabled}
            setAllSettingsEnabled={setAllSettingsEnabled}
            settings={settings}
            setSettings={setSettings}
            isEditable={isEditable}
            blacklistId={blacklistId}
            setValue={setValue}
            fields={fields}
          />
        ))}
      </div>
      <div className={cn.blockTitle}>Payment Orders</div>
      <div className={cn.formToggleGroupWrapper}>
        {paymentOrders.map((item) => (
          <SettingItem
            key={item.value}
            control={control}
            item={item}
            allSettingsEnabled={allSettingsEnabled}
            setAllSettingsEnabled={setAllSettingsEnabled}
            settings={settings}
            setSettings={setSettings}
            isEditable={isEditable}
            blacklistId={blacklistId}
            setValue={setValue}
            fields={fields}
          />
        ))}
      </div>
      <div className={cn.blockTitle}>
        Banks in Transactions
        <span>
          <div className={`${cn.blockHint} ${cn.lessLeft}`}>
            The bank from which the funds are received in case of incoming
            payment or the bank to which the funds are sent in case of outgoing
            payment
          </div>
          <IconInfo width={16} height={16} />
        </span>
      </div>
      {banksIntransactions.map((item) => (
        <SettingItem
          key={item.value}
          control={control}
          item={item}
          allSettingsEnabled={allSettingsEnabled}
          setAllSettingsEnabled={setAllSettingsEnabled}
          settings={settings}
          setSettings={setSettings}
          isEditable={isEditable}
          blacklistId={blacklistId}
          setValue={setValue}
          fields={fields}
        />
      ))}
      {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) &&
        settingsId && (
          <NavLink
            className={cn.changeLogLink}
            to={`/administration/change-logs?entityType=BLACKLIST_SETTING&entityId=${settingsId}`}
            target="_blank"
          >
            <IconChangeLoClock />
            Change Log
          </NavLink>
        )}
    </div>
  );
};

export default CreateBlacklistForm;
